<template>
  <v-data-table
    :headers="headers"
    :items="activeItems"
    class="elevation-1"
    :loading="isLoading"
    :items-per-page="-1"
    sort-by="stockItem.productionDate"
    :sort-desc="true"
    hide-default-footer
    dense
    no-data-text="No active process check sheets found"
  >
    <template v-slot:item.sizeRange="{ item }">
      <span v-if="item.stockItem">
        {{ item.stockItem.sizeMin }} - {{ item.stockItem.sizeMax }}
      </span>
    </template>
    <template v-slot:item.updatedBy="{ item }">
      <span v-if="item.updatedBy">{{ item.updatedBy }}</span>
      <span v-else>{{ item.createdBy }}</span>
    </template>
    <template v-slot:item.stockItem.receivedMass="{ item }">
      <span v-if="item.stockItem">
        {{ item.stockItem.receivedMass.toFixed(2) }}
      </span>
    </template>
    <template v-slot:item.stockItem.productionDate="{ item }">
      <span v-if="item.stockItem">
        {{ formatDateTime(item.stockItem.productionDate) }}
      </span>
    </template>
    <template v-slot:item.updated="{ item }">
      <span v-if="item.updated">{{ formatDateTime(item.updated) }}</span>
      <span v-else>{{ formatDateTime(item.created) }}</span>
    </template>
    <template v-slot:item.actions="{ item, index }">
      <v-btn
        v-if="item.stockItem && item.type === 'Process Check Sheet'"
        small
        :data-cy="`ListActiveProcessCheckSheets-${index}`"
        color="success"
        :to="{
          name: 'processing-EditProcessCheckSheet',
          query: { stockItemId: item.stockItem.id }
        }">
        Continue
      </v-btn>
      <v-btn
        v-if="item.stockItem && item.type === 'Can&Can End Inspection'"
        small
        :data-cy="`ListActiveProcessCheckSheets-${index}`"
        color="success"
        :to="{
          name: 'processing-EditCanEndInspection',
          query: { stockItemId: item.stockItem.id }
        }">
        Continue
      </v-btn>
      <v-btn
        v-if="item.stockItem && item.type === 'Pre-Cooked Weights'"
        small
        :data-cy="`ListActiveProcessCheckSheets-pre-cooked-weights-${index}`"
        color="success"
        :to="{
          name: 'processing-EditDriedAbaloneSummary',
          query: { id: item.id, stockItemId: item.stockItem.id }
        }">
        Continue
      </v-btn>
      <v-btn
        v-if="item.stockItem && item.type === 'Canning Process'"
        small
        :data-cy="`ListActiveProcessCheckSheets-canning-process-${index}`"
        color="success"
        :to="{
          name: 'processing-EditCanningProcess',
          query: { stockItemId: item.stockItem.id }
        }">
        Continue
      </v-btn>
      <v-btn
        v-if="item.stockItem && item.type === 'Batch Printing Verification'"
        small
        :data-cy="`ListActiveProcessCheckSheets-batch-printing-${index}`"
        color="success"
        :to="{
          name: 'processing-EditBatchPrintingVerification',
          query: { stockItemId: item.stockItem.id }
        }">
        Continue
      </v-btn>
      <v-btn
        v-if="item.stockItem && item.type === 'Dried Cooking Process Check'"
        small
        :data-cy="`ListActiveProcessCheckSheets-dried-cooking-process-${index}`"
        color="success"
        :to="{
          name: 'processing-EditDriedCookingProcessCheck',
          query: { id: item.id, stockItemId: item.stockItem.id }
        }">
        Continue
      </v-btn>
    </template>
  </v-data-table>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { mdiPencil, mdiDelete } from '@mdi/js';
import loading from '@codehq/aurora-app-core/src/mixins/loading';

export default {
  name: 'ListActiveProcessCheckSheets',
  module: 'ProcessCheckSheet',
  mixins: [loading],
  props: {
    statusList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      mdiPencil, mdiDelete,
    };
  },
  computed: {
    /** Vuex states
     */
    ...mapState('batchPrintingVerifications', ['batchPrintingVerifications']),
    ...mapState('processCheckSheets', ['processCheckSheets']),
    ...mapState('canEndInspections', ['canEndInspections']),
    ...mapState('canningProcesses', ['canningProcesses']),
    ...mapState('driedAbaloneSummaries', ['driedAbaloneSummaries']),
    ...mapState('driedCookingProcessChecks', ['driedCookingProcessChecks']),
    /**
    * Get table column headers
    */
    headers() {
      return [
        {
          text: 'Production Date',
          value: 'stockItem.productionDate',
        },
        {
          text: 'Type',
          value: 'type',
        },
        {
          text: 'Status',
          value: 'status',
        },
        {
          text: 'Size',
          value: 'sizeRange',
        },
        {
          text: 'Received Mass',
          value: 'stockItem.receivedMass',
        },
        {
          text: 'Type',
          value: 'stockItem.stockType',
        },
        {
          text: 'Last Updated by',
          value: 'updatedBy',
        },
        {
          text: 'Last Updated',
          value: 'updated',
        },
        {
          text: '',
          value: 'actions',
        },
      ];
    },
    activeDriedAbaloneSummaries() {
      return this.driedAbaloneSummaries
        .filter((item) => item.status !== 'Completed');
    },
    activeProcessCheckSheets() {
      return this.processCheckSheets
        .filter((item) => item.status !== 'Completed' && item.stockItem?.stockType !== 'Undefined');
    },
    activeBatchPrintingVerifications() {
      return this.batchPrintingVerifications
        .filter((item) => item.status !== 'Completed' && item.stockItem?.stockType !== 'Undefined');
    },
    activeCanEnd() {
      return this.canEndInspections.filter((item) => item.status !== 'Completed');
    },
    activeCanningProcess() {
      return this.canningProcesses.filter((item) => item.status !== 'Completed');
    },
    activeDriedCookingProcessChecks() {
      return this.driedCookingProcessChecks.filter((item) => item.status !== 'Completed');
    },
    activeItems() {
      const {
        // eslint-disable-next-line max-len
        activeProcessCheckSheets,
        activeCanEnd,
        activeCanningProcess,
        activeBatchPrintingVerifications,
        activeDriedAbaloneSummaries,
        activeDriedCookingProcessChecks,
      } = this;
      const result = [
        ...activeDriedAbaloneSummaries.map((s) => ({ ...s, type: 'Pre-Cooked Weights' })),
        ...activeProcessCheckSheets.map((s) => ({ ...s, type: 'Process Check Sheet' })),
        ...activeCanEnd.map((s) => ({ ...s, type: 'Can&Can End Inspection' })),
        ...activeCanningProcess.map((s) => ({ ...s, type: 'Canning Process' })),
        ...activeBatchPrintingVerifications.map((s) => ({ ...s, type: 'Batch Printing Verification' })),
        ...activeDriedCookingProcessChecks.map((s) => ({ ...s, type: 'Dried Cooking Process Check' })),
      ];

      if (this.statusList.length > 0) {
        return result.filter((item) => this.statusList.includes(item.status));
      }

      return result;
    },
  },
  async mounted() {
    this.isLoading = true;
    await Promise.all([
      (async () => {
        await this.LOAD_processCheckSheets();
      })(),
      (async () => {
        await this.LOAD_canningProcesses();
      })(),
      (async () => {
        await this.LOAD_canEndInspections();
      })(),
      (async () => {
        await this.LOAD_batchPrintingVerifications();
      })(),
      (async () => {
        await this.LOAD_driedAbaloneSummaries();
      })(),
      (async () => {
        await this.LOAD_driedCookingProcessChecks();
      })(),
    ]);
    this.isLoading = false;
  },
  methods: {
    /** Vuex methods
     */
    ...mapActions('processCheckSheets', ['LOAD_processCheckSheets', 'DELETE_processCheckSheet']),
    ...mapActions('canEndInspections', ['LOAD_canEndInspections']),
    ...mapActions('canningProcesses', ['LOAD_canningProcesses']),
    ...mapActions('batchPrintingVerifications', ['LOAD_batchPrintingVerifications']),
    ...mapActions('driedAbaloneSummaries', ['LOAD_driedAbaloneSummaries']),
    ...mapActions('driedCookingProcessChecks', ['LOAD_driedCookingProcessChecks']),
    /** Delete ProcessCheckSheet from the collection
     */
    async remove(ProcessCheckSheet) {
      this.isLoading = true;
      await this.DELETE_processCheckSheet(ProcessCheckSheet);
      this.isLoading = false;
    },
  },
};
</script>
